import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Avatar,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Notifications as NotificationsIcon,
  Lock as LockIcon,
  Save as SaveIcon,
  Visibility,
  VisibilityOff,
  Sms as SmsIcon,
  MailOutline as MailOutlineIcon,
  DesktopWindows as DesktopIcon,
  NotificationsActive as PushIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  AddCircleOutline as AddIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { profileApi, authApi, twoFactorApi, notificationApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { DATE_TIME_FORMAT_OPTIONS } from '../utils/dateTime';

const Profile: React.FC = () => {
  const { checkAuth } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Profile form state
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    name: '',
    phone: '',
    email: '',
    username: '',
  });

  // Notification preferences state
  const [notifications, setNotifications] = useState({
    notifySms: false,
    notifyEmail: true,
    notifyInApp: true,
    notifyUrgentOnly: false,
    notifyPush: false,
    notifyUrgencyLevels: [] as string[],
    quietHoursStart: '',
    quietHoursEnd: '',
    dndWindows: [] as Array<{ days: string[]; start: string; end: string }>,
    timezone: '',
    dateTimeFormat: 'mdy_12',
    tonePreference: '',
  });

  const [pushSupported, setPushSupported] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  // 2FA state
  const [twoFactor, setTwoFactor] = useState({
    enabled: false,
    method: null as string | null,
    hasPhone: false,
    hasEmail: false,
  });
  const [twoFactorDialogOpen, setTwoFactorDialogOpen] = useState(false);
  const [twoFactorStep, setTwoFactorStep] = useState<'method' | 'verify'>('method');
  const [twoFactorMethod, setTwoFactorMethod] = useState<'sms' | 'email'>('email');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [enabling2FA, setEnabling2FA] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordError, setPasswordError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetch2FAStatus();
  }, []);

  useEffect(() => {
    setPushSupported('serviceWorker' in navigator && 'PushManager' in window);
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await profileApi.getProfile();
      const data = response.data.data;
      
      setProfile({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        name: data.name || '',
        phone: data.phone || '',
        email: data.email || '',
        username: data.username || '',
      });

      setNotifications({
        notifySms: data.notifySms || false,
        notifyEmail: data.notifyEmail !== false,
        notifyInApp: data.notifyInApp !== false,
        notifyUrgentOnly: data.notifyUrgentOnly || false,
        notifyPush: data.notifyPush || false,
        notifyUrgencyLevels: data.notifyUrgencyLevels || [],
        quietHoursStart: data.quietHoursStart || '',
        quietHoursEnd: data.quietHoursEnd || '',
        dndWindows: data.dndWindows || [],
        timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || '',
        dateTimeFormat: data.dateTimeFormat || 'mdy_12',
        tonePreference: data.tonePreference || '',
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      setSnackbar({ open: true, message: 'Failed to load profile', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetch2FAStatus = async () => {
    try {
      const response = await twoFactorApi.getStatus();
      setTwoFactor(response.data.data);
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      await profileApi.updateProfile({
        firstName: profile.firstName,
        lastName: profile.lastName,
        name: profile.name || `${profile.firstName} ${profile.lastName}`.trim(),
        phone: profile.phone,
      });
      await checkAuth();
      setSnackbar({ open: true, message: 'Profile updated successfully', severity: 'success' });
    } catch (error) {
      console.error('Failed to update profile:', error);
      setSnackbar({ open: true, message: 'Failed to update profile', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    try {
      setSaving(true);
      await profileApi.updateNotificationPreferences({
        notifySms: notifications.notifySms,
        notifyEmail: notifications.notifyEmail,
        notifyInApp: notifications.notifyInApp,
        notifyUrgentOnly: notifications.notifyUrgentOnly,
        notifyPush: notifications.notifyPush,
        notifyUrgencyLevels: notifications.notifyUrgencyLevels,
        quietHoursStart: notifications.quietHoursStart || undefined,
        quietHoursEnd: notifications.quietHoursEnd || undefined,
        dndWindows: notifications.dndWindows,
        timezone: notifications.timezone || null,
        dateTimeFormat: notifications.dateTimeFormat || null,
        tonePreference: notifications.tonePreference || null,
      });
      await checkAuth();
      setSnackbar({ open: true, message: 'Notification preferences updated', severity: 'success' });
    } catch (error) {
      console.error('Failed to update notifications:', error);
      setSnackbar({ open: true, message: 'Failed to update notification preferences', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const urgencyOptions = [
    { value: 'urgent', label: 'Urgent' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
  ];

  const toneOptions = [
    { value: '', label: 'Default (auto)' },
    { value: 'formal', label: 'Formal' },
    { value: 'concise', label: 'Concise' },
    { value: 'empathetic', label: 'Empathetic' },
    { value: 'friendly', label: 'Friendly' },
    { value: 'short', label: 'Short' },
  ];

  const dndDays = [
    { value: 'mon', label: 'Mon' },
    { value: 'tue', label: 'Tue' },
    { value: 'wed', label: 'Wed' },
    { value: 'thu', label: 'Thu' },
    { value: 'fri', label: 'Fri' },
    { value: 'sat', label: 'Sat' },
    { value: 'sun', label: 'Sun' },
  ];

  const toggleUrgencyLevel = (level: string) => {
    setNotifications((prev) => {
      const current = prev.notifyUrgencyLevels || [];
      const next = current.includes(level)
        ? current.filter((item) => item !== level)
        : [...current, level];
      return { ...prev, notifyUrgencyLevels: next, notifyUrgentOnly: false };
    });
  };

  const addDndWindow = () => {
    setNotifications((prev) => ({
      ...prev,
      dndWindows: [...(prev.dndWindows || []), { days: [], start: '22:00', end: '06:00' }],
    }));
  };

  const updateDndWindow = (index: number, patch: Partial<{ days: string[]; start: string; end: string }>) => {
    setNotifications((prev) => {
      const updated = [...(prev.dndWindows || [])];
      updated[index] = { ...updated[index], ...patch };
      return { ...prev, dndWindows: updated };
    });
  };

  const toggleDndDay = (index: number, day: string) => {
    setNotifications((prev) => {
      const updated = [...(prev.dndWindows || [])];
      const currentDays = updated[index]?.days || [];
      const nextDays = currentDays.includes(day)
        ? currentDays.filter((item) => item !== day)
        : [...currentDays, day];
      updated[index] = { ...updated[index], days: nextDays };
      return { ...prev, dndWindows: updated };
    });
  };

  const removeDndWindow = (index: number) => {
    setNotifications((prev) => {
      const updated = [...(prev.dndWindows || [])];
      updated.splice(index, 1);
      return { ...prev, dndWindows: updated };
    });
  };

  const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i += 1) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const ensureServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) return null;
    const existing = await navigator.serviceWorker.getRegistration();
    if (existing) return existing;
    return navigator.serviceWorker.register('/service-worker.js');
  };

  const subscribeToPush = async () => {
    if (!pushSupported) {
      throw new Error('Push is not supported on this device.');
    }
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      throw new Error('Push permission not granted.');
    }
    const registration = await ensureServiceWorker();
    if (!registration) {
      throw new Error('Service worker not available.');
    }
    const keyResponse = await notificationApi.getPushPublicKey();
    const publicKey = keyResponse.data?.publicKey || keyResponse.data?.data?.publicKey;
    if (!publicKey) {
      throw new Error('Push public key not available.');
    }
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
    await notificationApi.subscribePush(subscription.toJSON ? subscription.toJSON() : subscription, navigator.userAgent);
  };

  const unsubscribeFromPush = async () => {
    if (!('serviceWorker' in navigator)) return;
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (subscription) {
      await notificationApi.unsubscribePush(subscription.endpoint);
      await subscription.unsubscribe();
    }
  };

  const handleTogglePush = async (enabled: boolean) => {
    setPushBusy(true);
    try {
      if (enabled) {
        await subscribeToPush();
        setNotifications((prev) => ({ ...prev, notifyPush: true }));
      } else {
        await unsubscribeFromPush();
        setNotifications((prev) => ({ ...prev, notifyPush: false }));
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || 'Push subscription failed',
        severity: 'error',
      });
      setNotifications((prev) => ({ ...prev, notifyPush: false }));
    } finally {
      setPushBusy(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    try {
      setChangingPassword(true);
      await authApi.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setSnackbar({ open: true, message: 'Password changed successfully', severity: 'success' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to change password';
      setPasswordError(message);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleEnable2FA = async () => {
    try {
      setEnabling2FA(true);
      const response = await twoFactorApi.enable(twoFactorMethod);
      setSnackbar({ open: true, message: response.data.message, severity: 'success' });
      setTwoFactorStep('verify');
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to send verification code', severity: 'error' });
    } finally {
      setEnabling2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    try {
      setEnabling2FA(true);
      const response = await twoFactorApi.verify(verificationCode);
      setBackupCodes(response.data.backupCodes || []);
      setSnackbar({ open: true, message: 'Two-factor authentication enabled!', severity: 'success' });
      await fetch2FAStatus();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Invalid verification code', severity: 'error' });
    } finally {
      setEnabling2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    try {
      setEnabling2FA(true);
      await twoFactorApi.disable(disablePassword);
      setSnackbar({ open: true, message: 'Two-factor authentication disabled', severity: 'success' });
      setDisableDialogOpen(false);
      setDisablePassword('');
      await fetch2FAStatus();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to disable 2FA', severity: 'error' });
    } finally {
      setEnabling2FA(false);
    }
  };

  const close2FADialog = () => {
    setTwoFactorDialogOpen(false);
    setTwoFactorStep('method');
    setVerificationCode('');
    setBackupCodes([]);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 2, px: 2 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        My Profile
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your account settings and preferences
      </Typography>

      <Grid container spacing={3}>
        {/* Profile Information Card */}
        <Grid size={{ xs: 12 }}>
          <Card elevation={2}>
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  {profile.firstName?.charAt(0) || profile.email?.charAt(0) || 'U'}
                </Avatar>
              }
              title={
                <Typography variant="h6">
                  <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Profile Information
                </Typography>
              }
              subheader="Update your personal details"
            />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={profile.firstName}
                    onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={profile.lastName}
                    onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={profile.username || profile.email}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Username cannot be changed"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={profile.email}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Email cannot be changed"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    placeholder="(305) 555-0100"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Used for SMS notifications and 2FA"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                      onClick={handleSaveProfile}
                      disabled={saving}
                    >
                      Save Profile
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Two-Factor Authentication Card */}
        <Grid size={{ xs: 12 }}>
          <Card elevation={2}>
            <CardHeader
              title={
                <Typography variant="h6">
                  <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Two-Factor Authentication
                </Typography>
              }
              subheader="Add an extra layer of security to your account"
              action={
                twoFactor.enabled ? (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Enabled"
                    color="success"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    icon={<WarningIcon />}
                    label="Disabled"
                    color="warning"
                    variant="outlined"
                  />
                )
              }
            />
            <Divider />
            <CardContent>
              {twoFactor.enabled ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Two-factor authentication is enabled via {twoFactor.method?.toUpperCase()}
                  </Alert>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setDisableDialogOpen(true)}
                  >
                    Disable 2FA
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Protect your account with two-factor authentication. You'll need to enter a verification code
                    sent to your phone or email when signing in.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SecurityIcon />}
                    onClick={() => setTwoFactorDialogOpen(true)}
                  >
                    Enable Two-Factor Authentication
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Preferences Card */}
        <Grid size={{ xs: 12 }}>
          <Card elevation={2}>
            <CardHeader
              title={
                <Typography variant="h6">
                  <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Notification Preferences
                </Typography>
              }
              subheader="Choose how you want to receive alerts for new messages"
            />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <SmsIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600}>SMS</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Receive text messages
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={notifications.notifySms}
                          onChange={(e) => setNotifications({ ...notifications, notifySms: e.target.checked })}
                          color="primary"
                        />
                      }
                      label={notifications.notifySms ? 'On' : 'Off'}
                    />
                    {notifications.notifySms && !profile.phone && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Add phone number above
                      </Alert>
                    )}
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <MailOutlineIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600}>Email</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Receive email alerts
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={notifications.notifyEmail}
                          onChange={(e) => setNotifications({ ...notifications, notifyEmail: e.target.checked })}
                          color="primary"
                        />
                      }
                      label={notifications.notifyEmail ? 'On' : 'Off'}
                    />
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <DesktopIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600}>In-App</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Browser notifications
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={notifications.notifyInApp}
                          onChange={(e) => setNotifications({ ...notifications, notifyInApp: e.target.checked })}
                          color="primary"
                        />
                      }
                      label={notifications.notifyInApp ? 'On' : 'Off'}
                    />
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <PushIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600}>Push</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Mobile & browser alerts
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={notifications.notifyPush}
                          onChange={(e) => handleTogglePush(e.target.checked)}
                          color="primary"
                          disabled={!pushSupported || pushBusy}
                        />
                      }
                      label={notifications.notifyPush ? 'On' : 'Off'}
                    />
                    {!pushSupported && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Push not supported on this device
                      </Alert>
                    )}
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Urgency levels
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select which task priorities should trigger alerts (none selected = no alerts).
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {urgencyOptions.map((option) => {
                      const selected = notifications.notifyUrgencyLevels.includes(option.value);
                      return (
                        <Chip
                          key={option.value}
                          label={option.label}
                          color={selected ? (option.value === 'urgent' ? 'error' : option.value === 'high' ? 'warning' : 'primary') : 'default'}
                          variant={selected ? 'filled' : 'outlined'}
                          onClick={() => toggleUrgencyLevel(option.value)}
                        />
                      );
                    })}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Do not disturb
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Block alerts during these time windows (timezone applied below).
                  </Typography>
                  {notifications.dndWindows.length === 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No DND windows configured. Add one if you need quiet hours.
                    </Alert>
                  )}
                  {notifications.dndWindows.map((window, index) => (
                    <Paper key={`${window.start}-${window.end}-${index}`} variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">Window {index + 1}</Typography>
                        <IconButton size="small" onClick={() => removeDndWindow(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                        {dndDays.map((day) => {
                          const selected = window.days?.includes(day.value);
                          return (
                            <Chip
                              key={`${index}-${day.value}`}
                              label={day.label}
                              variant={selected ? 'filled' : 'outlined'}
                              color={selected ? 'primary' : 'default'}
                              onClick={() => toggleDndDay(index, day.value)}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            label="Start"
                            type="time"
                            value={window.start}
                            onChange={(e) => updateDndWindow(index, { start: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            label="End"
                            type="time"
                            value={window.end}
                            onChange={(e) => updateDndWindow(index, { end: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addDndWindow}
                    sx={{ mb: 2 }}
                  >
                    Add DND window
                  </Button>
                  <TextField
                    fullWidth
                    label="Timezone (IANA)"
                    value={notifications.timezone}
                    onChange={(e) => setNotifications({ ...notifications, timezone: e.target.value })}
                    helperText="Example: America/New_York"
                  />
                  <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => setNotifications({ ...notifications, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '' })}
                    >
                      Use device timezone
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Display & AI Tone Preferences */}
        <Grid size={{ xs: 12 }}>
          <Card elevation={2}>
            <CardHeader
              title={
                <Typography variant="h6">
                  <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Display & AI Tone
                </Typography>
              }
              subheader="Customize date/time format and AI tone"
            />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="date-format-label">Date/Time Format</InputLabel>
                    <Select
                      labelId="date-format-label"
                      label="Date/Time Format"
                      value={notifications.dateTimeFormat}
                      onChange={(e) => setNotifications({ ...notifications, dateTimeFormat: e.target.value })}
                    >
                      {DATE_TIME_FORMAT_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel id="tone-format-label">Tone Preference</InputLabel>
                    <Select
                      labelId="tone-format-label"
                      label="Tone Preference"
                      value={notifications.tonePreference}
                      onChange={(e) => setNotifications({ ...notifications, tonePreference: e.target.value })}
                    >
                      {toneOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">
                    Tone preference influences suggested responses and consultation drafts.
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                      onClick={handleSaveNotifications}
                      disabled={saving}
                    >
                      Save Preferences
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Change Password Card */}
        <Grid size={{ xs: 12 }}>
          <Card elevation={2}>
            <CardHeader
              title={
                <Typography variant="h6">
                  <LockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Change Password
                </Typography>
              }
              subheader="Update your password for account security"
            />
            <Divider />
            <CardContent>
              {passwordError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {passwordError}
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                            edge="end"
                          >
                            {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    helperText="Min 8 characters"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                            edge="end"
                          >
                            {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    error={passwordForm.confirmPassword !== '' && passwordForm.newPassword !== passwordForm.confirmPassword}
                    helperText={
                      passwordForm.confirmPassword !== '' && passwordForm.newPassword !== passwordForm.confirmPassword
                        ? 'Passwords do not match'
                        : ''
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                            edge="end"
                          >
                            {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="warning"
                      startIcon={changingPassword ? <CircularProgress size={20} color="inherit" /> : <LockIcon />}
                      onClick={handleChangePassword}
                      disabled={changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                    >
                      Change Password
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Enable 2FA Dialog */}
      <Dialog open={twoFactorDialogOpen} onClose={close2FADialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {backupCodes.length > 0 ? 'Save Your Backup Codes' : 'Enable Two-Factor Authentication'}
        </DialogTitle>
        <DialogContent>
          {backupCodes.length > 0 ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Save these backup codes in a safe place. You can use them to access your account if you lose access to your phone or email.
              </Alert>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.100' }}>
                <Grid container spacing={1}>
                  {backupCodes.map((code, index) => (
                    <Grid size={{ xs: 6 }} key={index}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {code}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>
          ) : twoFactorStep === 'method' ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Choose how you want to receive verification codes:
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={twoFactorMethod === 'sms'}
                    onChange={() => setTwoFactorMethod('sms')}
                  />
                }
                label="SMS (text message)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={twoFactorMethod === 'email'}
                    onChange={() => setTwoFactorMethod('email')}
                  />
                }
                label="Email"
              />
              {twoFactorMethod === 'sms' && !profile.phone && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Please add a phone number to your profile before enabling SMS 2FA.
                </Alert>
              )}
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Enter the 6-digit verification code sent to your {twoFactorMethod === 'sms' ? 'phone' : 'email'}:
              </Typography>
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="123456"
                inputProps={{ maxLength: 6 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {backupCodes.length > 0 ? (
            <Button onClick={close2FADialog} variant="contained">
              I've Saved My Codes
            </Button>
          ) : twoFactorStep === 'method' ? (
            <>
              <Button onClick={close2FADialog}>Cancel</Button>
              <Button
                onClick={handleEnable2FA}
                variant="contained"
                disabled={enabling2FA || (twoFactorMethod === 'sms' && !profile.phone)}
              >
                {enabling2FA ? <CircularProgress size={20} /> : 'Send Code'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setTwoFactorStep('method')}>Back</Button>
              <Button
                onClick={handleVerify2FA}
                variant="contained"
                disabled={enabling2FA || verificationCode.length !== 6}
              >
                {enabling2FA ? <CircularProgress size={20} /> : 'Verify & Enable'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={disableDialogOpen} onClose={() => setDisableDialogOpen(false)}>
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your password to confirm disabling 2FA:
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDisable2FA}
            color="error"
            variant="contained"
            disabled={enabling2FA || !disablePassword}
          >
            {enabling2FA ? <CircularProgress size={20} /> : 'Disable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile;



